import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { slugifyLabel } from '@shared/utils/slug';
import type { BuilderMenuLocation, BuilderPageType, ProjectDetailRecord } from '@shared/types';
import { useI18n } from '@renderer/shared/i18n';
import { useUiStore } from '@renderer/stores/ui-store';
import {
  createBuilderDocument,
  deleteBuilderDocument,
  setPrimaryBuilderDocument,
  updateBuilderDocument
} from '../projects-repository';
import { useProjectBuilderStore } from './project-builder-store';

interface CreatePageDraft {
  title: string;
  slug: string;
  pageType: BuilderPageType;
  menuLocation: BuilderMenuLocation;
  slugDirty: boolean;
}

function createEmptyDraft(title: string, pageType: BuilderPageType, menuLocation: BuilderMenuLocation = 'none'): CreatePageDraft {
  return {
    title,
    slug: slugifyLabel(title),
    pageType,
    menuLocation,
    slugDirty: false
  };
}

function resolveBuilderActionMessage(error: unknown, fallbackMessage: string): string {
  if (!(error instanceof Error)) {
    return fallbackMessage;
  }

  if (error.message.includes('Database is unavailable')) {
    return 'Builder storage is currently unavailable. Please retry in a moment. If the problem persists, restart the app and verify local database access.';
  }

  return error.message;
}

export function useProjectBuilderController({
  detail,
  onDetailChange,
  builderFlushRef
}: {
  detail: ProjectDetailRecord;
  onDetailChange: (detail: ProjectDetailRecord) => void;
  builderFlushRef?: MutableRefObject<(() => Promise<void>) | null>;
}) {
  const { t } = useI18n();
  const addToast = useUiStore((state) => state.addToast);
  const hydrate = useProjectBuilderStore((state) => state.hydrate);
  const setSaving = useProjectBuilderStore((state) => state.setSaving);
  const pages = useProjectBuilderStore((state) => state.pages);
  const dirtyPageIds = useProjectBuilderStore((state) => state.dirtyPageIds);
  const staleValidationPageIds = useProjectBuilderStore((state) => state.staleValidationPageIds);
  const isSaving = useProjectBuilderStore((state) => state.isSaving);
  const selectedPageId = useProjectBuilderStore((state) => state.selectedPageId);
  const selectPage = useProjectBuilderStore((state) => state.selectPage);
  const validateStalePages = useProjectBuilderStore((state) => state.validateStalePages);

  const activePage = useMemo(() => pages.find((record) => record.id === selectedPageId) ?? null, [pages, selectedPageId]);
  const savePromiseRef = useRef<Promise<void> | null>(null);
  const [isCreatePageDialogOpen, setCreatePageDialogOpen] = useState(false);
  const [isDeletePageDialogOpen, setDeletePageDialogOpen] = useState(false);
  const [createPageDraft, setCreatePageDraft] = useState<CreatePageDraft>(() =>
    createEmptyDraft(t('project.builder.dialogs.create.defaults.standardTitle'), 'standard')
  );

  const buildCreatePageDraft = useCallback(
    (pageCount: number): CreatePageDraft => {
      if (pageCount === 0) {
        return createEmptyDraft(t('project.builder.dialogs.create.defaults.homepageTitle'), 'homepage', 'none');
      }

      return createEmptyDraft(t('project.builder.dialogs.create.defaults.standardTitle'), 'standard', 'none');
    },
    [t]
  );

  useEffect(() => {
    hydrate(detail);
  }, [detail, hydrate]);

  const mergeSavedPage = useCallback((savedDetail: ProjectDetailRecord, savedPageId: string) => {
    const nextRecord = savedDetail.builderDocuments.find((record) => record.id === savedPageId);
    if (!nextRecord) {
      return;
    }

    useProjectBuilderStore.setState((state) => {
      const nextDirtyPageIds = state.dirtyPageIds.filter((entry) => entry !== savedPageId);
      return {
        pages: state.pages.map((record) => (record.id === savedPageId ? nextRecord : record)),
        dirtyPageIds: nextDirtyPageIds,
        staleValidationPageIds: state.staleValidationPageIds.filter((entry) => entry !== savedPageId),
        isDirty: nextDirtyPageIds.length > 0
      };
    });
  }, []);

  const flushPendingSaves = useCallback(async () => {
    if (savePromiseRef.current) {
      await savePromiseRef.current;
      return;
    }

    useProjectBuilderStore.getState().validateStalePages();

    const snapshot = useProjectBuilderStore.getState();

    if (snapshot.dirtyPageIds.length === 0) {
      return;
    }

    const savePromise = (async () => {
      setSaving(true);

      try {
        for (const pageId of [...useProjectBuilderStore.getState().dirtyPageIds]) {
          const currentState = useProjectBuilderStore.getState();
          const page = currentState.pages.find((record) => record.id === pageId);

          if (!page) {
            continue;
          }

          const nextDetail = await updateBuilderDocument({
            id: page.id,
            title: page.document.pageMeta.title,
            slug: page.document.pageMeta.slug,
            status: page.document.pageMeta.status,
            document: page.document
          });

          onDetailChange(nextDetail);
          mergeSavedPage(nextDetail, page.id);
        }
      } catch (error) {
      const message = resolveBuilderActionMessage(error, t('project.builder.toast.saveFailed'));
        addToast({
          title: t('project.builder.toast.title'),
          message,
          tone: 'danger'
        });
        throw error;
      } finally {
        setSaving(false);
        savePromiseRef.current = null;
      }
    })();

    savePromiseRef.current = savePromise;
    await savePromise;
  }, [addToast, mergeSavedPage, onDetailChange, setSaving, t]);

  useEffect(() => {
    if (dirtyPageIds.length === 0 || isSaving) {
      return;
    }

    const timeout = window.setTimeout(() => {
      void flushPendingSaves();
    }, 700);

    return () => window.clearTimeout(timeout);
  }, [dirtyPageIds, flushPendingSaves, isSaving]);

  useEffect(() => {
    if (staleValidationPageIds.length === 0) {
      return;
    }

    const timeout = window.setTimeout(() => {
      validateStalePages();
    }, 350);

    return () => window.clearTimeout(timeout);
  }, [staleValidationPageIds, validateStalePages]);

  useEffect(() => {
    if (!builderFlushRef) {
      return;
    }

    builderFlushRef.current = () => flushPendingSaves();

    return () => {
      builderFlushRef.current = null;
    };
  }, [builderFlushRef, flushPendingSaves]);

  const openCreatePageDialog = useCallback(() => {
    setCreatePageDraft(buildCreatePageDraft(pages.length));
    setCreatePageDialogOpen(true);
  }, [buildCreatePageDraft, pages.length]);

  const closeCreatePageDialog = useCallback(() => {
    setCreatePageDialogOpen(false);
    setCreatePageDraft(buildCreatePageDraft(pages.length));
  }, [buildCreatePageDraft, pages.length]);

  const updateCreatePageTitle = useCallback((value: string) => {
    setCreatePageDraft((current) => ({
      ...current,
      title: value,
      slug: current.slugDirty ? current.slug : slugifyLabel(value),
      slugDirty: current.slugDirty
    }));
  }, []);

  const updateCreatePageSlug = useCallback((value: string) => {
    setCreatePageDraft((current) => ({
      ...current,
      slug: slugifyLabel(value),
      slugDirty: true
    }));
  }, []);

  const updateCreatePageType = useCallback((value: BuilderPageType) => {
    setCreatePageDraft((current) => ({
      ...current,
      pageType: value
    }));
  }, []);

  const updateCreatePageMenuLocation = useCallback((value: BuilderMenuLocation) => {
    setCreatePageDraft((current) => ({
      ...current,
      menuLocation: value
    }));
  }, []);

  const submitCreatePage = useCallback(async () => {
    const nextTitle = createPageDraft.title.trim();
    if (!nextTitle) {
      addToast({
        title: t('project.builder.toast.title'),
        message: t('project.builder.dialogs.create.validation.titleRequired'),
        tone: 'warning'
      });
      return;
    }

    await flushPendingSaves();

    try {
      const nextSlug = slugifyLabel(createPageDraft.slug.trim() || nextTitle);
      const nextDetail = await createBuilderDocument({
        projectId: detail.project.id,
        title: nextTitle,
        slug: nextSlug,
        pageType: createPageDraft.pageType,
        menuLocation: createPageDraft.menuLocation
      });

      onDetailChange(nextDetail);
      hydrate(nextDetail);
      const createdPage =
        nextDetail.builderDocuments.find((record) => record.slug === nextSlug) ??
        nextDetail.builderDocuments.find((record) => record.title === nextTitle) ??
        nextDetail.builderDocuments[nextDetail.builderDocuments.length - 1] ??
        null;

      if (createdPage) {
        selectPage(createdPage.id);
      }

      setCreatePageDialogOpen(false);
      setCreatePageDraft(buildCreatePageDraft(nextDetail.builderDocuments.length));
    } catch (error) {
      const message = resolveBuilderActionMessage(error, t('project.builder.toast.createFailed'));
      addToast({
        title: t('project.builder.toast.title'),
        message,
        tone: 'danger'
      });
    }
  }, [addToast, buildCreatePageDraft, createPageDraft, detail.project.id, flushPendingSaves, hydrate, onDetailChange, selectPage, t]);

  const openDeletePageDialog = useCallback(() => {
    if (!activePage || pages.length <= 1) {
      return;
    }

    setDeletePageDialogOpen(true);
  }, [activePage, pages.length]);

  const closeDeletePageDialog = useCallback(() => {
    setDeletePageDialogOpen(false);
  }, []);

  const confirmDeletePage = useCallback(async () => {
    if (!activePage || pages.length <= 1) {
      return;
    }

    await flushPendingSaves();

    try {
      const nextDetail = await deleteBuilderDocument({ id: activePage.id });
      onDetailChange(nextDetail);
      hydrate(nextDetail);
      setDeletePageDialogOpen(false);
    } catch (error) {
      const message = resolveBuilderActionMessage(error, t('project.builder.toast.deleteFailed'));
      addToast({
        title: t('project.builder.toast.title'),
        message,
        tone: 'danger'
      });
    }
  }, [activePage, addToast, flushPendingSaves, hydrate, onDetailChange, pages.length, t]);

  const setPrimaryPage = useCallback(async () => {
    if (!activePage || activePage.isPrimary) {
      return;
    }

    await flushPendingSaves();

    try {
      const nextDetail = await setPrimaryBuilderDocument({
        projectId: detail.project.id,
        documentId: activePage.id
      });
      onDetailChange(nextDetail);
      hydrate(nextDetail);
    } catch (error) {
      const message = resolveBuilderActionMessage(error, t('project.builder.toast.primaryFailed'));
      addToast({
        title: t('project.builder.toast.title'),
        message,
        tone: 'danger'
      });
    }
  }, [activePage, addToast, detail.project.id, flushPendingSaves, hydrate, onDetailChange, t]);

  return {
    activePage,
    canDeletePage: pages.length > 1,
    createPageDraft,
    flushPendingSaves,
    isCreatePageDialogOpen,
    isDeletePageDialogOpen,
    openCreatePageDialog,
    closeCreatePageDialog,
    updateCreatePageTitle,
    updateCreatePageSlug,
    updateCreatePageType,
    updateCreatePageMenuLocation,
    submitCreatePage,
    openDeletePageDialog,
    closeDeletePageDialog,
    confirmDeletePage,
    setPrimaryPage
  };
}
